import staffService from "services/staffService";
import volunteerService from "services/volunteerService";
import { EVENTS } from "lib/events";

/**
 * The Staff and Volunteers admin modules are the same pages driven by one of
 * these configs. Routes pass `audience="staff"|"volunteer"`; pages resolve it
 * with `getAudience` and hand the config to their components.
 *
 * - `service`     API client for the audience (see services/assessmentService)
 * - `basePath`    admin route prefix; profile links are `${basePath}/people/:id`
 * - `intakePath`  public intake route prefix (App.tsx)
 * - `events`      Mixpanel events and the property name for the profile id
 * - `labels`      every audience-specific word shown in the UI
 */
export const AUDIENCES = {
  staff: {
    audience: "staff",
    service: staffService,
    basePath: "/admin/staff",
    intakePath: "/staff-intake",
    events: {
      assessmentViewed: EVENTS.STAFF_PAGE_VIEWED,
      profileViewed: EVENTS.STAFF_PROFILE_VIEWED,
      profileIdProperty: "staff_id",
      assessmentSent: EVENTS.STAFF_ASSESSMENT_SENT,
      checkInViewed: EVENTS.STAFF_CHECKIN_VIEWED,
      weeklyCheckInViewed: EVENTS.STAFF_PULSE_CARE_VIEWED,
    },
    labels: {
      // "Staff Directory", "Send Staff Digest"
      adjective: "Staff",
      // "Active Staff", column headers
      plural: "Staff",
      // running copy: "search staff", "developing staff in this area"
      pluralLower: "staff",
      // fallback when a person has no role
      role: "Staff",
    },
  },
  volunteer: {
    audience: "volunteer",
    service: volunteerService,
    basePath: "/admin/volunteers",
    intakePath: "/volunteer-intake",
    events: {
      assessmentViewed: EVENTS.VOLUNTEERS_PAGE_VIEWED,
      profileViewed: EVENTS.VOLUNTEER_PROFILE_VIEWED,
      profileIdProperty: "volunteer_id",
      assessmentSent: EVENTS.VOLUNTEER_ASSESSMENT_SENT,
      checkInViewed: EVENTS.VOLUNTEER_CHECKIN_VIEWED,
      weeklyCheckInViewed: EVENTS.VOLUNTEER_PULSE_CARE_VIEWED,
    },
    labels: {
      adjective: "Volunteer",
      plural: "Volunteers",
      pluralLower: "volunteers",
      role: "Volunteer",
    },
  },
};

/** Config for `audience`; throws on an unknown value so a bad route fails loudly. */
export const getAudience = (audience) => {
  const config = AUDIENCES[audience];
  if (!config) throw new Error(`Unknown assessment audience: ${audience}`);
  return config;
};

/** Profile page path for a user in this audience. */
export const profilePath = (config, userId) => `${config.basePath}/people/${userId}`;
